import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: '1. Introduction',
      content: `AppStoreZone ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application discovery and download platform ("AppStoreZone" or "the Software").

By downloading, installing, or using AppStoreZone, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not download, install, or use the Software.`
    },
    {
      title: '2. Information We Collect',
      content: `AppStoreZone collects minimal information necessary to provide our services:

Information We Automatically Collect:
• Device information (operating system version, hardware type) for compatibility purposes
• Anonymous usage statistics such as which app categories are most browsed
• Download metrics to improve our content recommendations
• Crash reports and error logs for troubleshooting

Information We DO NOT Collect:
• Personal identification information unless you voluntarily provide it when contacting support
• Your browsing history outside of the AppStoreZone application
• Keystroke data, screenshots, or screen recordings
• Financial information or payment details
• Contacts, calendar, photos, or other personal data

Information You May Voluntarily Provide:
• Contact information when reaching out to our support team
• Feedback, suggestions, or bug reports you choose to submit`
    },
    {
      title: '3. How We Use Your Information',
      content: `We use the information we collect for the following purposes:

Service Delivery:
• To provide, maintain, and improve the AppStoreZone application
• To personalize your experience and recommend relevant applications
• To process and fulfill download requests

Product Improvement:
• To understand how users interact with our platform
• To identify and fix technical issues
• To develop new features and improve existing ones

Communication:
• To respond to your support requests and inquiries
• To send important updates about the Software (security patches, critical updates)
• We do not send marketing communications unless you explicitly opt in`
    },
    {
      title: '4. Data Storage and Security',
      content: `Security Measures:
• All data transmissions are encrypted using industry-standard TLS/SSL protocols
• We implement appropriate technical and organizational security measures
• Regular security audits and vulnerability assessments
• Access to user data is restricted to authorized personnel only

Data Storage:
• Application preferences are stored locally on your device
• Anonymous analytics data may be stored on secure servers
• We use reputable cloud providers that maintain SOC 2 and ISO 27001 certifications

Your Control Over Data:
• You can clear all local app data through your device settings
• Uninstalling AppStoreZone removes all locally stored data
• You can opt out of anonymous analytics in the app settings`
    },
    {
      title: '5. Third-Party Services',
      content: `AppStoreZone facilitates the download of third-party applications. Please be aware:

Third-Party Applications:
• Each application you download through AppStoreZone has its own privacy policy
• We encourage you to review the privacy policy of each application before downloading
• We are not responsible for the privacy practices of third-party applications

Analytics Partners:
• We may use third-party analytics services to help us understand usage patterns
• These services collect anonymous, aggregated data only
• No personally identifiable information is shared with analytics partners

We Do NOT:
• Sell your information to third parties under any circumstances
• Share your data with advertisers or marketing companies
• Use third-party tracking pixels or advertising SDKs
• Participate in data broker networks or exchanges`
    },
    {
      title: '6. Your Rights and Choices',
      content: `You have the following rights regarding your information:

Access and Control:
• Access locally stored data through app settings
• Opt out of anonymous analytics collection
• Request information about what data we hold about you

Data Deletion:
• Clear all local data by uninstalling the application
• Request deletion of any data we may have on our servers by contacting us

For Users in the European Economic Area (EEA):
Under GDPR, you have additional rights including:
• Right to access, rectification, and erasure of your personal data
• Right to restrict or object to processing
• Right to data portability
• Right to withdraw consent at any time

For Users in California:
Under CCPA, you have the right to:
• Know what personal information is collected
• Request deletion of your personal information
• Opt out of the sale of personal information (we do not sell personal information)`
    },
    {
      title: '7. Children\'s Privacy',
      content: `AppStoreZone is not directed at children under the age of 13, and we do not knowingly collect personal information from children under 13.

If you are a parent or guardian and believe that your child has provided us with personal information without your consent, please contact us immediately at support@appstorezone.com.

Upon verification, we will promptly delete any personal information from our records.`
    },
    {
      title: '8. Cookies and Tracking',
      content: `The AppStoreZone desktop application does not use browser cookies. However:

• We may use local storage on your device to save your preferences and settings
• Our website (appstorezone.com) may use standard cookies for functionality and analytics
• You can manage cookie preferences through your browser settings

We do not use cookies or similar technologies for advertising or cross-site tracking purposes.`
    },
    {
      title: '9. Changes to This Privacy Policy',
      content: `We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements.

When We Make Changes:
• We will update the "Last Updated" date at the bottom of this policy
• For significant changes, we will provide notice through the application
• Continued use of AppStoreZone after changes constitutes acceptance

Previous versions of this policy are available upon request.`
    },
    {
      title: '10. Contact Us',
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:

Email: support@appstorezone.com
Website: https://appstorezone.com

AppStoreZone
Yigal Alon St 94, Tel Aviv–Yafo

Last Updated: March 2026`
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0e17] text-[#ebedfb] font-body">
      {/* Ambient Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-[20%] w-[500px] h-[500px] rounded-full blur-[160px]" style={{ background: 'radial-gradient(circle, rgba(137,172,255,0.08) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[20%] left-[20%] w-[400px] h-[400px] rounded-full blur-[160px]" style={{ background: 'radial-gradient(circle, rgba(145,147,255,0.06) 0%, transparent 70%)' }} />
      </div>

      {/* Navbar */}
      <nav className="relative z-50 px-6 py-5" style={{ background: '#0f131d' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-2xl font-extrabold tracking-tight font-display" style={{ letterSpacing: '-0.02em' }}>
              <span className="text-[#ebedfb]">App</span>
              <span className="text-[#89acff]">Store</span>
              <span className="text-[#ebedfb]">Zone</span>
            </span>
          </Link>
          <Link to="/" className="text-[#a7abb7] hover:text-[#ebedfb] transition-colors duration-300 font-medium flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Header */}
      <header className="relative z-10 py-16 px-6" style={{ background: '#0f131d' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: [0.2, 0.8, 0.2, 1] }}
            className="flex items-center gap-5"
          >
            <div className="p-4 rounded-xl" style={{ background: '#1a1f2c' }}>
              <Shield className="w-8 h-8 text-[#89acff]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold font-display" style={{ letterSpacing: '-0.02em' }}>Privacy Policy</h1>
              <p className="text-[#a7abb7] mt-2">Your privacy matters. Here's how we protect it.</p>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
                className="p-8 rounded-2xl"
                style={{ background: '#151925' }}
              >
                <h2 className="text-xl font-bold mb-4 text-[#ebedfb] font-display">{section.title}</h2>
                <div className="text-[#a7abb7] leading-relaxed whitespace-pre-line text-[0.95rem]">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-14 px-6" style={{ background: '#0f131d' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <span className="text-lg font-extrabold tracking-tight font-display" style={{ letterSpacing: '-0.02em' }}>
            <span className="text-[#ebedfb]">App</span>
            <span className="text-[#89acff]">Store</span>
            <span className="text-[#ebedfb]">Zone</span>
          </span>
          <div className="flex gap-8 text-sm text-[#a7abb7]">
            <Link to="/privacy" className="text-[#ebedfb]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#ebedfb] transition-colors duration-300">Terms of Service</Link>
          </div>
          <p className="text-[#444852] text-sm">&copy; {new Date().getFullYear()} AppStoreZone. Yigal Alon St 94, Tel Aviv–Yafo.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
